import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./EditInternalResourceCost.css";
import axiosInstance from "../../../Config/axiosInstance";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { NumericFormat } from "react-number-format";

const EditInternalResourceCost = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    project_code: "",
    cost_id: "",
    year: "",
    month: "",
    internal_resource: "",
    hour_worked: "",
  });

  const animatedComponents = makeAnimated();
  const [projectCodes, setProjectCodes] = useState([]);
  const [allCosts, setAllCosts] = useState([]);
  const [filteredCosts, setFilteredCosts] = useState([]);
  const [monthChoices, setMonthChoices] = useState([]);
  const [yearChoices, setYearChoices] = useState([]);
  const [internalResources, setInternalResources] = useState([]);

  // Preluare coduri proiect
  useEffect(() => {
    axiosInstance
      .get('/project-codes/')
      .then(response => {
        const formattedProjectCodes = response.data.map(pc => ({
          value: pc.id,
          label: `${pc.code} - ${pc.description}`
        }));
        setProjectCodes(formattedProjectCodes);
      })
      .catch(error => console.error("Eroare la preluarea codurilor de proiect:", error));
  }, []);

  // Preluare toate livrabilele
  useEffect(() => {
    axiosInstance
      .get('/budget-structure/')
      .then(response => {
        const formattedCosts = response.data.map(dl => ({
          value: dl.id,
          label: `${dl.cost_element_id} - ${dl.cost_element_description}`,
          project_code: dl.project_code_details?.id
        }));
        setAllCosts(formattedCosts);
      })
      .catch(error => console.error("Eroare la preluarea livrabilelor:", error));
  }, []);

  // Filtrare livrabile cand se schimba project_code
  useEffect(() => {
    if (formData.project_code) {
      const filtered = allCosts.filter(dl =>
        dl.project_code === formData.project_code
      );
      setFilteredCosts(filtered);
    } else {
      setFilteredCosts(allCosts);
    }
  }, [formData.project_code, allCosts]);

  // Preluare luni
  useEffect(() => {
    axiosInstance
      .get("/month-choices/")
      .then((response) => {
        if (response.data.month_choices) {
          const options = response.data.month_choices.map(([value, label]) => ({
            value,
            label
          }));
          setMonthChoices(options);
        }
      })
      .catch((error) => console.error("Error fetching month choices:", error));
  }, []);

  // Preluare ani
  useEffect(() => {
    axiosInstance
      .get("/year-choices/")
      .then((response) => {
        if (response.data.year_choices) {
          const options = response.data.year_choices.map(([value, label]) => ({
            value,
            label
          }));
          setYearChoices(options);
        }
      })
      .catch((error) => console.error("Error fetching year choices:", error));
  }, []);

  // Preluare resurse interne
  useEffect(() => {
    axiosInstance
      .get('/internal-resources/')
      .then(response => {
        const formattedInternalResources = response.data.map(ir => ({
          value: ir.id,
          label: `${ir.resource_name}`
        }));
        setInternalResources(formattedInternalResources);
      })
      .catch(error => console.error("Eroare la preluarea resurselor interne:", error));
  }, []);

  // Fetch data for edit
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get(`/internal-resource-cost/${id}/`);
        const data = response.data;

        setFormData({
          project_code: data.project_code,
          cost_id: data.cost_id,
          year: data.year,
          month: data.month,
          internal_resource: data.internal_resource,
          hour_worked: data.hour_worked,
        });

        // Dacă avem deja toate livrabilele, filtrăm după project_code
        if (data.project_code && allCosts.length > 0) {
          const filtered = allCosts.filter(dl =>
            dl.project_code === data.project_code
          );
          setFilteredCosts(filtered);
        }
      } catch (error) {
        console.error("Eroare la preluarea datelor resursei interne pentru editare:", error);
      }
    };

    fetchData();
  }, [id, allCosts]);

  // handleChange păstrat
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`/internal-resource-cost/${id}/`, formData);
      navigate(-1);
    } catch (error) {
      console.error("Error updating:", error.response?.data || error.message);
    }
  };

  return (
    <div className="edit-internal-resource-cost-page">
      <div className="edit-internal-resource-cost-container">
        <div className="header">
          <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
          <h2 className="edit-title">Edit Internal Resource Cost</h2>
        </div>
      </div>
      <hr className="title-separator" />
      <div className="edit-internal-resource-cost-container">
        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-group">
            <label htmlFor="project_code" className="label">
              Project Code <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="project_code"
              name="project_code"
              classNamePrefix="react-select"
              options={projectCodes}
              required
              value={projectCodes.find((c) => c.value === formData.project_code) || null}
              onChange={(selectedOption) => {
                setFormData({
                  ...formData,
                  project_code: selectedOption ? selectedOption.value : "",
                  cost_id: ""
                });
              }}
              components={animatedComponents}
              isClearable
              styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="cost_id" className="label">
              Cost ID <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="cost_id"
              name="cost_id"
              classNamePrefix="react-select"
              options={filteredCosts}
              required
              value={filteredCosts.find((c) => c.value === formData.cost_id) || null}
              onChange={(selectedOption) => setFormData({
                ...formData,
                cost_id: selectedOption ? selectedOption.value : ""
              })}
              components={animatedComponents}
              isClearable
              isDisabled={!formData.project_code}
              styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
            />
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="year" className="label">
                Year <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="year"
                name="year"
                classNamePrefix="react-select"
                options={yearChoices}
                required
                value={yearChoices.find((option) => option.value === formData.year) || null}
                onChange={(selectedOption) => setFormData({
                  ...formData,
                  year: selectedOption ? selectedOption.value : ""
                })}
                components={animatedComponents}
                isClearable
                styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="month" className="label">
                Month <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="month"
                name="month"
                classNamePrefix="react-select"
                options={monthChoices}
                required
                value={monthChoices.find((option) => option.value === formData.month) || null}
                onChange={(selectedOption) => setFormData({
                  ...formData,
                  month: selectedOption ? selectedOption.value : ""
                })}
                components={animatedComponents}
                isClearable
                styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="internal_resource" className="label">
              Internal Resource <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="internal_resource"
              name="internal_resource"
              classNamePrefix="react-select"
              options={internalResources}
              required
              value={internalResources.find((option) => option.value === formData.internal_resource) || null}
              onChange={(selectedOption) => setFormData({
                ...formData,
                internal_resource: selectedOption ? selectedOption.value : ""
              })}
              components={animatedComponents}
              isClearable
              styles={{ control: (provided) => ({ ...provided, borderRadius: "8px" }) }}
            />
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="hour_worked" className="label">
                Hour Worked on internal resource <span style={{ color: 'red' }}>*</span>
              </label>

              {/* NumericFormat DAR păstrăm handleChange */}
              <NumericFormat
                id="hour_worked"
                name="hour_worked"
                value={formData.hour_worked}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                onValueChange={({ value }) => {
                  // value = string numeric cu punct la zecimale (ex: "1234.50")
                  handleChange({ target: { name: "hour_worked", value: value || "" } });
                }}
                required
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="button-group-inline">
              <button type="button" className="button discard" onClick={() => navigate(-1)}>
                Discard
              </button>
              <button type="submit" className="button save">Save</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInternalResourceCost;
